import React, { ReactElement, useContext, useMemo } from 'react';
import { TFunction } from 'i18next';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { GlobalContext } from '../../../pages/_app';
import EditForm from 'components/editForm/EditForm';
import {
    BasicCountryEnum,
    EditFieldInterface,
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    Option,
    StateInterface,
    ValidateValueType,
    ValueOnChangeRemoveType,
    ValuesInterface,
    ValuesStringInterface,
} from 'components/interfaces/GeneralInterface';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import {
    parseCzechBirthNumber,
    slashCzechBirthNumber,
    validateNoWhitespaceOnly,
} from 'utils/index';
import { countryCodeToLanguageCode } from 'utils/helpers/countryCodeToLanguageCode';
import { getGenderSelectValues } from 'utils/helpers/getGenderSelectValues';
import {
    AddressApiInterface,
    FieldTypeApiEnum,
    MemberRegistrationApiInterface,
    RepresentativeRelationEnum,
} from 'submodules/api_middleware';
import { getCountryFormField } from 'components/forms/editFormFields/getCountryFormField';
import { getPostalCodeFormField } from 'components/forms/editFormFields/getPostalCodeFormField';
import { validateLength } from 'utils/helpers/validateLength';
import { getRoute } from 'constants/routes';
import {
    EditFormDataInterface,
    OnSuccessData,
} from 'components/interfaces/EditFormInterface';
import { ROW_BREAK } from 'components/editForm/utils/items';
import { TNamespaceEnum } from 'types/types';
import { FormResponseData, UserService } from 'services/user.service';
import stylesBasicBlock from 'components/blocks/BasicDetailsBlock.module.scss';
import { ClubsAccessLevel, findClubs } from 'utils/helpers/browser/filter';
import Info from 'components/login/signup/Info';
import { getTokenCookie } from 'utils/helpers/browser/cookies';
import { TokenPurpose } from 'utils/helpers/cookies.shared';

interface Props {
    values: ValuesInterface;
    validateCzechPhoneFormat: boolean;
    /**
     * Should clicking on the button of the form submit a membership application?
     * It means the user is signed in,  and either is a minor, or an adult that has not yet been
     * registered, and we register him/her additionally.
     */
    shouldRegister: boolean;
    registrationModeInfoKey: string | null;
    existingAddress: AddressApiInterface | false;
    getFormData: (data: EditFormDataInterface) => void;
    getRequestData: () => MemberRegistrationApiInterface;
    goNext: () => void;
    goBack: () => void;
    birthdate: string;
    nationality: string;
    languagesList: Option[];
    countriesList: Option[];
    isMinor: boolean;
}

/**
 * A component where user can edit identifiers (national id, birth country, sex)
 * and permanent address. There is a checkbox for players that displays
 * additional fields (birth city, second nationality, language).
 */
const SignUpPersonalDetails = ({
    values,
    validateCzechPhoneFormat,
    shouldRegister,
    registrationModeInfoKey,
    existingAddress,
    getFormData,
    getRequestData,
    goNext,
    goBack,
    countriesList,
    languagesList,
    birthdate,
    nationality,
    isMinor,
}: Props): ReactElement => {
    const { t } = useTranslation();
    const router = useRouter();

    function secondNationalityCountriesList(): Option[] {
        return countriesList.filter((e) => e.value != values.nationality);
    }

    const fields = useMemo<GroupEditFieldInterface[]>(
        (): GroupEditFieldInterface[] =>
            getFieldWithLocalization(
                t,
                countriesList,
                secondNationalityCountriesList(),
                languagesList,
                shouldRegister,
                existingAddress,
                values,
                birthdate,
                nationality,
                validateCzechPhoneFormat,
                isMinor
            ),
        [
            countriesList,
            languagesList,
            validateCzechPhoneFormat,
            birthdate,
            nationality,
            shouldRegister,
            values['copy_permanent_address'],
        ]
    );

    // The submit/action button label.
    const buttonTextKey = shouldRegister
        ? getTranslationKey(TNamespaceEnum.signup, 'signUp')
        : 'general.next';

    const submitApplication = async (
        values: ValuesStringInterface
    ): Promise<FormResponseData> => {
        const data = getRequestData();
        if (values.register_as_player) {
            // TODO: IT-677 - enable
            // data.person.photo = values.photo;
        }
        data.payment_url = `${location.origin}/application-payment`;
        return await new UserService().registerAdditionalMember(data);
    };

    // Move to the payment address
    const onApplicationSuccess: OnSuccessData = async (
        _,
        __,
        response: FormResponseData
    ) => {
        const applicationId = response.data.id;
        const discipline = response.data.discipline;
        await router.push(
            getRoute('/application-payment', [], null, {
                applicationId,
                discipline,
            })
        );
    };

    // Zjištění, zda je uživatel přihlášený
    const isUserLoggedIn = !!getTokenCookie(TokenPurpose.Access);
    /**
     *   When the registrationModeInfoKey not null, it displays an info box with a message.
     *   The text is localized, and based on the registrationModeInfoKey parameter.
     */
    const getRegistrationModeInfo = () => {
        if (registrationModeInfoKey !== null) {
            return (
                <div className={classNames(stylesBasicBlock.sizeP20)}>
                    {isUserLoggedIn ? <Info /> : null}
                </div>
            );
        }
        return <div />;
    };

    return (
        <EditForm
            fields={fields}
            buttonTextKey={buttonTextKey}
            submit={shouldRegister ? submitApplication : undefined}
            getSideBlock={getRegistrationModeInfo}
            onSuccess={onApplicationSuccess}
            action={shouldRegister ? undefined : goNext}
            goBack={goBack}
            getFormData={getFormData}
        />
    );
};

export default SignUpPersonalDetails;

const onCopyAddressChange = (
    value: ValueOnChangeRemoveType,
    state: StateInterface,
    existingAddress: AddressApiInterface | false
): StateInterface => {
    //Propagation of the toggle button change back to the caller:
    state.copy_permanent_address = value;
    //And the actual setting the address fields to the existing address, or their resetting:
    if (existingAddress !== false && value) {
        state.street = existingAddress.street;
        state.street_number = existingAddress.street_number;
        state.city = existingAddress.city;
        state.postal_code = existingAddress.postal_code;
        state.country_address = existingAddress.country_code;
    } else {
        state.street = '';
        state.street_number = '';
        state.city = '';
        state.postal_code = '';
        state.country_address = undefined;
    }
    return state;
};

const getPermanentAddressFields = (
    t: TFunction,
    values: ValuesInterface,
    shouldRegister: boolean,
    existingAddress: AddressApiInterface | false,
    countriesList: Option[]
): GroupEditFieldInterface => {
    let permanentAddressFields: EditFieldInterface[] = [];

    // Also when BE failed to deliver the address, we simply do not display the toggle button:
    if (shouldRegister && existingAddress !== false) {
        const { user } = useContext(GlobalContext);
        const hasAdult = user.user_persons.find((x) => !x.registered_as_minor);

        const copyPermanentAddressLabelKey = hasAdult
            ? 'permanentAddressFromAdult'
            : 'permanentAddressFromMinor';

        permanentAddressFields = [
            {
                name: 'copy_permanent_address',
                value: values['copy_permanent_address'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    copyPermanentAddressLabelKey
                ),
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: false,
                onChange: (value, state) =>
                    onCopyAddressChange(value, state, existingAddress),
            },
            ROW_BREAK,
        ];
    }

    const copyAddress =
        !!values['copy_permanent_address'] && existingAddress !== false;

    return {
        title: getTranslationKey(TNamespaceEnum.signup, 'permanentAddress'),
        fields: [
            ...permanentAddressFields,
            {
                name: 'street',
                value: values['street'],
                label: 'address.street',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: !copyAddress,
                disabled: copyAddress,
            },
            {
                name: 'street_number',
                value: values['street_number'],
                label: 'address.streetNumber',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: !copyAddress,
                disabled: copyAddress,
            },
            {
                name: 'city',
                value: values['city'],
                label: 'address.city',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: !copyAddress,
                disabled: copyAddress,
            },
            {
                ...getPostalCodeFormField(
                    values.postal_code as string,
                    t,
                    'country_address'
                ),
                name: 'postal_code',
                required: !copyAddress,
                disabled: copyAddress,
            },
            {
                ...getCountryFormField(
                    values.country_address as string,
                    countriesList
                ),
                name: 'country_address',
                placeholder: 'filters.choose',
                noEmptyOption: undefined,
                required: !copyAddress,
                disabled: copyAddress,
            },
        ],
    };
};

const getFieldWithLocalization = (
    t: TFunction,
    countriesList: Option[],
    countriesListSecondNationality: Option[],
    languagesList: Option[],
    shouldRegister: boolean,
    existingAddress: AddressApiInterface | false,
    values: ValuesInterface,
    birthdate: string,
    nationality: string,
    validateCzechPhoneFormat: boolean,
    isMinor: boolean
): GroupEditFieldInterface[] => [
    {
        title: 'pages.personDetails',
        globalValidation: (state) => {
            if (!validateCzechPhoneFormat) return;

            // Get the gender
            const gender = state['gender'];
            if (!gender) return undefined;

            // Get the birthday
            const parsedBirthDate = new Date(
                Date.parse(birthdate + 'T00:00:00')
            );

            // Get the birth id
            const nationalId = state['national_identifier'];

            // Validate the national id
            const validationRes = parseCzechBirthNumber(String(nationalId));

            if (validationRes.valid) {
                // Validate gender
                if (validationRes.gender != gender)
                    return t('validations.birthNumberWrongGender');

                // Validate birthdate
                if (
                    validationRes.birthDate.getTime() !=
                    parsedBirthDate.getTime()
                )
                    return t('validations.birthNumberWrongBirthDate');
            }

            return undefined;
        },
        fields: [
            {
                name: 'national_identifier',
                value: values['national_identifier'],
                label: getTranslationKey(
                    TNamespaceEnum.person,
                    'nationalIdentifier'
                ),
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: nationality === BasicCountryEnum.CZ,
                validate: (value: ValidateValueType) => {
                    const val = String(value);
                    const validationRes = parseCzechBirthNumber(val);

                    // Invalid id
                    if (!validationRes.valid)
                        return t(validationRes.errorMessageKey);

                    return undefined;
                },
                onChange: (
                    value: ValueOnChangeRemoveType,
                    state: StateInterface
                ) => {
                    const strVal = String(value);
                    state.national_identifier = slashCzechBirthNumber(
                        validateCzechPhoneFormat,
                        strVal
                    );
                    return state;
                },
            },
            {
                name: 'passport',
                value: values['passport'],
                label: getTranslationKey(
                    TNamespaceEnum.person,
                    'numberOfIdOrPassport'
                ),
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: nationality !== BasicCountryEnum.CZ,
                displayIfSuccess: () => nationality !== BasicCountryEnum.CZ,
                validate: (value: ValidateValueType) => {
                    const val = String(value);
                    return (
                        validateLength(val, 20, t) ??
                        validateNoWhitespaceOnly(t, val)
                    );
                },
            },
            {
                name: 'birth_country_code',
                value: values['birth_country_code'],
                label: getTranslationKey(TNamespaceEnum.person, 'birthCountry'),
                type: FieldTypeApiEnum.SELECT,
                size: FilterSize.S20,
                placeholder: 'filters.choose',
                values: countriesList,
                labelSize: LabelSize.TOP,
                required: true,
                onChange: (
                    value: ValueOnChangeRemoveType,
                    state: StateInterface
                ) => ({
                    ...state,
                    language: state.language
                        ? state.language
                        : countryCodeToLanguageCode(value as string),
                    birth_country_code: value,
                }),
            },
            {
                name: 'gender',
                value: values['gender'],
                label: getTranslationKey(TNamespaceEnum.person, 'gender'),
                type: FieldTypeApiEnum.SELECT,
                size: FilterSize.S20,
                placeholder: 'filters.choose',
                values: getGenderSelectValues(t),
                labelSize: LabelSize.TOP,
                required: true,
            },
            {
                name: 'representative_relation',
                value: values['representative_relation'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'representativeRelationToRegisteredMinorLabel'
                ),
                type: FieldTypeApiEnum.RADIO,
                size: FilterSize.FULL,
                labelSize: LabelSize.TOP,
                values: [
                    RepresentativeRelationEnum.PARENT,
                    RepresentativeRelationEnum.GUARDIAN,
                    RepresentativeRelationEnum.CUSTODIAN,
                    RepresentativeRelationEnum.OTHER,
                ].map(
                    (type): Option => ({
                        label: t(`RepresentativeRelation.${type}`),
                        value: type,
                    })
                ),
                required: true,
                displayIfSuccess: () => isMinor,
            },
        ],
    },
    getPermanentAddressFields(
        t,
        values,
        shouldRegister,
        existingAddress,
        countriesList
    ),
    {
        title: '',
        fields: [
            {
                name: 'targito_agreement',
                value: values['targito_agreement'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'targitoAgreement'
                ),
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.S20,
            },
            ROW_BREAK,
            {
                name: 'register_as_player',
                value: values['register_as_player'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'fields.registerAsPlayer'
                ),
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.S20,
            },
            ROW_BREAK,
            {
                name: 'birthCity',
                value: values['birthCity'],
                label: 'address.birthCity',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: true,
                displayIfSuccess: ({ register_as_player }) =>
                    !!register_as_player,
            },
            {
                name: 'nationality2',
                value: values['nationality2'],
                label: getTranslationKey(TNamespaceEnum.person, 'nationality2'),
                type: FieldTypeApiEnum.SELECT,
                size: FilterSize.S20,
                placeholder: getTranslationKey(
                    TNamespaceEnum.signup,
                    'noSecondNationality'
                ),
                values: countriesListSecondNationality,
                labelSize: LabelSize.TOP,
                displayIfSuccess: ({ register_as_player }) =>
                    !!register_as_player,
            },
            {
                name: 'language',
                value: values['language'],
                label: getTranslationKey(TNamespaceEnum.person, 'language'),
                type: FieldTypeApiEnum.SELECT,
                size: FilterSize.S20,
                placeholder: 'filters.choose',
                values: languagesList,
                labelSize: LabelSize.TOP,
                required: true,
                displayIfSuccess: ({ register_as_player }) =>
                    !!register_as_player,
            },
            ROW_BREAK,
            {
                name: 'register_as_club_member',
                value: values['register_as_club_member'],
                //Clear the selected club when the preference has changed:
                onChange: (value, state) => {
                    if (!value) {
                        return {
                            ...state,
                            register_as_club_member: value,
                            club_id: undefined,
                        };
                    } else {
                        return {
                            ...state,
                            register_as_club_member: value,
                        };
                    }
                },
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'fields.registerAsClubMember'
                ),
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.S20,
            },
            ROW_BREAK,
            {
                name: 'club_id',
                label: getTranslationKey(TNamespaceEnum.signup, 'fields.club'),
                type: FieldTypeApiEnum.AUTOCOMPLETE,
                size: FilterSize.FULL,
                labelSize: LabelSize.TOP,
                displayIfSuccess: ({ register_as_club_member }) =>
                    !!register_as_club_member,
                requiredIfSuccess: (state) =>
                    !!state['register_as_club_member'],
                initValuesCb: () => findClubs({}, ClubsAccessLevel.All),
                onSearch: (query) =>
                    findClubs(
                        {
                            ...query,
                        },
                        ClubsAccessLevel.All
                    ),
            },
            ROW_BREAK,
            {
                name: 'marketing_agreement',
                value: values['marketing_agreement'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'marketingAgreement'
                ),
                linkInBrackets: {
                    label: getTranslation(
                        t,
                        TNamespaceEnum.signup,
                        'marketingAgreementLink'
                    ),
                    url: getRoute('/clenstvi-podminky'),
                },
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.S20,
            },
            ROW_BREAK,
            {
                name: 'agree',
                value: values['agree'],
                label: getTranslationKey(TNamespaceEnum.signup, 'agree'),
                linkInBrackets: {
                    label: getTranslation(
                        t,
                        TNamespaceEnum.signup,
                        'agreeLink'
                    ),
                    url: getRoute('/clenstvi-podminky'),
                },
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.S20,
                required: true,
            },
        ],
    },
];
